import { forwardRef, type ComponentProps, type ReactNode } from 'react';
import { Link } from '@/components/link';
import { focusVisibleOutline } from '@/lib/focus-styles';
import { cn } from '@/lib/utils';
import { ProductTileImageCustom } from '@/components/product-tile-custom/product-tile-image-custom';
import type { ShopperSearch } from '@/scapi';
import type { TFunction } from 'i18next';
import { ProductTileTagOverflowRow } from './product-tile-tag-overflow-row';

type SearchHit = ShopperSearch.schemas['ProductSearchHit'];

export interface ProductTileCustomEventCardProps extends ComponentProps<'div'> {
    product: SearchHit;
    productName: string;
    className?: string;
    brand?: string;
    productUrl: string;
    effectiveImgAspectRatio: number;
    handleProductClick?: (product: SearchHit) => void;
    productHighlight?: string;
    eatingList: string[];
    priceAndWeight: ReactNode;
    t: TFunction<'product'>;
}

/**
 * Figma card-product (37:37205): image + txt (title, .bar info, add button) with spacing/200 between sections.
 */
function ProductTileCustomEventCardInner(
    {
        product,
        productName,
        brand,
        productUrl,
        effectiveImgAspectRatio,
        handleProductClick,
        productHighlight,
        eatingList,
        t,
        priceAndWeight,
        className,
        ...rest
    }: ProductTileCustomEventCardProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    return (
        <div
            ref={ref}
            data-testid="product-tile"
            className={cn(
                'relative h-full w-full min-w-0 max-w-full cursor-default gap-0 border-0 py-0 min-h-[474px]',
                'flex flex-col gap-0 rounded-3xl bg-card text-card-foreground overflow-hidden',
                className
            )}
            {...rest}>
            <div className="absolute left-0 top-0 w-full h-full z-0">
                <ProductTileImageCustom
                    variant="plp-vertical"
                    product={product}
                    pdpLink={false}
                    imgAspectRatio={effectiveImgAspectRatio}
                    handleProductClick={handleProductClick}
                />
            </div>
            <div className="absolute top-0 right-0 w-full pointer-events-none flex min-w-0 flex-1 flex-wrap justify-end items-center gap-1 p-6">
                <ProductTileTagOverflowRow
                    highlight={productHighlight}
                    eatingStyles={eatingList}
                    productType={'event'}
                    // Reserve a consistent row height even when no tags.
                    className="w-fit"
                />
            </div>
            {/* add overlay from bottom to top with gradient */}
            <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col justify-end items-end z-1 text-neutral-white">
                <div className="flex flex-col gap-4 p-6 bg-gradient-to-b from-transparent to-black">
                    <div className="flex w-full min-w-0 flex-col gap-1">
                        <p className="body-copy-m m-0 w-full min-w-0 text-neutral-gray-40">{brand || '\u00A0'}</p>
                        <h3 className="title-l m-0 w-full min-w-0 text-left">{productName}</h3>
                    </div>
                    {priceAndWeight}
                </div>
            </div>
            <Link
                to={productUrl}
                className={cn('absolute inset-0 z-10 rounded-3xl', focusVisibleOutline)}
                aria-label={t('viewProductAriaLabel', { productName }) ?? `View ${productName}`}
                onClick={() => handleProductClick?.(product)}
            />
        </div>
    );
}

const ProductTileCustomEventCard = forwardRef(ProductTileCustomEventCardInner);

export { ProductTileCustomEventCard };
